import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fTimeDateUSFormat } from '@/actions/Helper';
import { ReactSVG } from 'react-svg';
import { Icons } from '@/assets';
import clsx from 'clsx';
import LoadingV2 from '@/components/Loading';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import Modal from '@/ctComponents/Modal';
import styles from './styles';
import { useNotify } from 'react-admin';
import { SaveApiResMessage } from '@/actions';
import HintLegend, {
  getHintLegendByOriginStatus,
  HintLegendItem,
} from '@/components/HintLegend';
import IconButtonV2 from '@/ctComponents/IconButton/IconButtonV2';

const AppointmentIcon = React.memo(({ appointment, index }) => {
  if (!appointment) return null;
  const isUsed = appointment.status === 'USED';
  const margin = isUsed ? '-10px' : 'unset';
  const src = isUsed ? Icons.tickAppointment : Icons.hidden;

  return <ReactSVG style={{ margin }} src={src} />;
});

const AppointmentButtonIcon = React.memo(({ type, cls }) => {
  const iconMap = {
    autobook: Icons.inventoryAptAutobook,
    book: Icons.inventoryAptBook,
    notAvailable: Icons.inventoryAptNotAvail,
  };

  return <ReactSVG src={iconMap[type]} className={cls.appointmentIcon} />;
});

const PickupAppointmentIcon = React.memo(({ status }) => {
  const isUsed = status === 'USED';
  const margin = isUsed ? '-10px' : 'unset';
  const src = isUsed ? Icons.tickAppointment : Icons.hidden;

  return <ReactSVG style={{ margin }} src={src} />;
});

const SortIcon = React.memo(
  ({ sortConfig, columnKey, hoveredColumn, cls, onSort, sortKey }) => {
    const iconSrc = useMemo(() => {
      if (sortConfig.key === columnKey) {
        return sortConfig.direction === 'ascending'
          ? Icons.sortedNewUp
          : Icons.sortedNewDown;
      }
      return Icons.sortNew;
    }, [sortConfig.key, sortConfig.direction, columnKey]);

    const isVisible = useMemo(
      () => hoveredColumn === columnKey || sortConfig.key === columnKey,
      [hoveredColumn, columnKey, sortConfig.key]
    );

    const handleClick = useCallback(
      (e) => {
        e.stopPropagation();
        onSort(sortKey);
      },
      [onSort, sortKey]
    );

    return (
      <ReactSVG
        src={iconSrc}
        style={{
          width: '12px',
          height: '12px',
          // marginLeft: '4px',
          cursor: 'pointer',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}
        className={clsx(
          cls.sortIcon,
          sortConfig.key === columnKey && cls.sortIconVisible
        )}
        onClick={handleClick}
      />
    );
  }
);

const CircularLoader = React.memo(
  ({ timerKey, timerinterval, fastProgress, cls }) => {
    return (
      <svg
        width="25"
        height="25"
        viewBox="0 0 36 36"
        className={cls.circularLoader}>
        <path
          className={cls.circleBackground}
          d="M18 2.0845
  a 15.9155 15.9155 0 0 1 0 31.831
  a 15.9155 15.9155 0 0 1 0 -31.831"
        />
        <path
          d="M18 2.0845
  a 15.9155 15.9155 0 0 1 0 31.831
  a 15.9155 15.9155 0 0 1 0 -31.831"
          className={cls.circleFastProgress}
          strokeDasharray={`${fastProgress}, 100`}
        />
        <path
          className={cls.circleProgress}
          strokeDasharray={`${timerinterval[timerKey] / 6.2}, 100`}
          d="M18 2.0845
  a 15.9155 15.9155 0 0 1 0 31.831
  a 15.9155 15.9155 0 0 1 0 -31.831"
        />
      </svg>
    );
  }
);

const AppointmentContainer = React.memo(
  ({ container, cls, Appointment, i }) => {
    if (!container?.appointments?.length) return null;

    const firstAppointment = container.appointments[0];
    const secondAppointment = container.appointments[1];

    return (
      <button
        className={clsx(cls.butReturnApt)}
        style={{
          border:
            container.autobook_from > 0
              ? '1px solid #1FC16B'
              : '1px solid #a2a2a2',
        }}>
        {container.autobook_on === 1 && (
          <span
            style={{
              borderTopLeftRadius: '0px',
              borderBottomLeftRadius: '0px',
            }}
            className="absolute top-0 rounded right-0 flex w-2 justify-center items-center bg-success-secondary text-white h-full">
            <span
              style={{
                marginRight: '-1px',
              }}
              className=" text-8 rotate-90 flex justify-center items-center">
              Autobook
            </span>
          </span>
        )}
        <div className={cls.appointmentContainer}>
          <div className={cls.appointmentDateContainer}>
            <span>
              <AppointmentIcon appointment={firstAppointment} index={0} />
            </span>
            <span className="flex flex-row items-center gap-1 justify-between">
              <span
                className="flex flex-row items-center gap-1 h-2 w-36 justify-between"
                style={{
                  width: '128px',
                }}>
                <span className={cls.appointmentDateContainerDates}>
                  {firstAppointment?.terminal_appointment_availability?.date?.replace(
                    /(\d{4})-(\d{2})-(\d{2})/,
                    '$2-$3-$1'
                  )}
                </span>
                <span className={cls.appointmentDateContainerTimes}>
                  {' '}
                  {`(${firstAppointment?.terminal_appointment_availability?.time_start?.replace(
                    /\s?(AM|PM)/gi,
                    ''
                  )}-${firstAppointment?.terminal_appointment_availability?.time_end?.replace(
                    /\s?(AM|PM)/gi,
                    ''
                  )})`}
                </span>{' '}
              </span>
              <span className="border-r pr-1 border-solid border-b-0 border-l-0 border-t-0 border-border-tertiary">
                <HintLegendItem
                  isSmall
                  hint={getHintLegendByOriginStatus(
                    firstAppointment?.origin_source
                  )}
                />
              </span>
            </span>
            <span
              className={`${cls.appointmenTerminal} w-24 ${
                container?.autobook_on === 1 ? 'pr-4' : ''
              }`}>
              {firstAppointment?.terminal_appointment_availability?.terminal
                ?.slug || firstAppointment?.terminal?.slug}
            </span>{' '}
          </div>
          {secondAppointment && (
            <div className={`${cls.appointmentDateContainer} mt-02`}>
              <span>
                <AppointmentIcon appointment={secondAppointment} index={1} />
              </span>
              <span className="flex flex-row items-center gap-1 h-2 justify-between">
                <span
                  className="flex flex-row items-center gap-1 h-2 w-36 justify-between"
                  style={{
                    width: '128px',
                  }}>
                  <span className={cls.appointmentDateContainerDates}>
                    {secondAppointment?.terminal_appointment_availability?.date?.replace(
                      /(\d{4})-(\d{2})-(\d{2})/,
                      '$2-$3-$1'
                    )}
                  </span>
                  <span className={cls.appointmentDateContainerTimes}>
                    {' '}
                    {`(${secondAppointment?.terminal_appointment_availability?.time_start?.replace(
                      /\s?(AM|PM)/gi,
                      ''
                    )}-${secondAppointment?.terminal_appointment_availability?.time_end?.replace(
                      /\s?(AM|PM)/gi,
                      ''
                    )})`}
                  </span>
                </span>
                <span className="border-r pr-1 border-solid border-b-0 border-l-0 border-t-0 border-border-tertiary">
                  <HintLegendItem
                    isSmall
                    hint={getHintLegendByOriginStatus(
                      secondAppointment?.origin_source
                    )}
                  />
                </span>
              </span>
              <span
                className={`${cls.appointmenTerminal} w-24 ${
                  container?.autobook_on === 1 ? 'pr-4' : ''
                }`}>
                {secondAppointment?.terminal?.slug}
              </span>{' '}
            </div>
          )}
          {container?.appointments?.length > 2 && (
            <div
              className={cls.moreAppointments}
              style={{
                fontSize: '9px',
                color: '#335CFF',
                fontFamily: 'Averta',
                padding: '0px',
                lineHeight: '9px',
              }}>
              +{container?.appointments?.length - 2} more
            </div>
          )}
        </div>
      </button>
    );
  }
);

const TableLoadList = ({
  cls,
  tableRef,
  amirbios2Ref,
  amirbios1Ref,
  tableTheadTitle,
  containersData,
  currentPage,
  uniqueIndex,
  formatDateTimeToUTC,
  formatDateToUTC,
  formatPrice,
  fDate,
  fTimeDate,
  enable_appointments,
  Appointment,
  shareLink,
  handleGetShareLink,
  Edit,
  delContainerLoad,
  handleChangeContainerStatus,
  timerinterval,
  fastProgress,
  getActivContainers,
  showContainersModal,
  setShowContainersModal,
  setSelectedRow,
  Del,
  mptyTrackingEnable,
  activeTab,
  Sizes = [],
  SSLs = [],
  updateContainer,
}) => {
  const notify = useNotify();
  let localUniqueIndex = uniqueIndex;

  // Sorting state
  const [sortConfig, setSortConfig] = useState(() => {
    // Load sort state from localStorage on component mount
    const savedSort = localStorage.getItem('inventoryTableSort');
    if (savedSort) {
      try {
        return JSON.parse(savedSort);
      } catch (error) {
        console.error('Error parsing saved sort state:', error);
        return { key: null, direction: '' };
      }
    }
    return { key: null, direction: '' };
  });
  const [sortedData, setSortedData] = useState([]);
  const [hoveredColumn, setHoveredColumn] = useState(null);

  // Temporary values for optimistic updates
  const [tempValues, setTempValues] = useState({});

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState({
    show: false,
    containerId: null,
  });

  // Handle container name click - copy to clipboard and open link after 2 seconds
  const handleContainerNameClick = useCallback((e, container, objectKey) => {
    e.preventDefault();
    const containerNumber = container[objectKey] || '-';
    const trackingUrl = container['shipping_line_tracking_url'];

    // Copy container number to clipboard
    navigator.clipboard
      .writeText(containerNumber)
      .then((res) => {
        // Show notification
        // notify(`Container Number "${containerNumber}" Copied`, 'success');
        SaveApiResMessage({
          title: res?.data?.messageHeader,
          message: `Container Number "${containerNumber}" Copied`,
          mode: 'success',
        });

        // Open link in new tab after 2 seconds
        setTimeout(() => {
          window.open(trackingUrl, '_blank');
        }, 1000);
      })
      .catch((err) => {
        // Fallback if clipboard API fails
        // notify(`Container Number "${containerNumber}" Copied`, 'success');
        SaveApiResMessage({
          title: 'Error Copying Container',
          message: `Container Number "${containerNumber}" Not Copied`,
          mode: 'success',
        });
        setTimeout(() => {
          window.open(trackingUrl, '_blank');
        }, 1000);
      });
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(() => {
    if (showDeleteModal.containerId) {
      delContainerLoad(showDeleteModal.containerId);
      setShowDeleteModal({ show: false, containerId: null });
    }
  }, [showDeleteModal.containerId, delContainerLoad]);

  // Handle delete button click - show confirmation modal
  const handleDeleteClick = useCallback((containerId) => {
    setShowDeleteModal({ show: true, containerId });
  }, []);

  // Clear temp values when data changes (fresh data from server)
  useEffect(() => {
    if (containersData && Object.keys(tempValues).length > 0) {
      // Check which temp values should be cleared based on fresh data
      setTempValues((prev) => {
        const newTemp = { ...prev };
        let hasChanges = false;

        Object.keys(prev).forEach((key) => {
          const [containerId, type] = key.split('_');
          const container = containersData.find(
            (c) => c.id.toString() === containerId
          );

          if (container) {
            // Check if server data matches our temp value
            const serverValue =
              type === 'size'
                ? container.specification?.size_type
                : container.company?.name;

            const tempValue = prev[key];

            // If server has updated data that matches our temp value, clear temp
            if (serverValue && tempValue && serverValue === tempValue.label) {
              delete newTemp[key];
              hasChanges = true;
            }
          }
        });

        return hasChanges ? newTemp : prev;
      });
    }
  }, [containersData, tempValues]);

  // Handle optimistic update
  const handleSelectChange = useCallback(
    async (containerId, type, selectedValue) => {
      // Optimistic update - change immediately
      setTempValues((prev) => ({
        ...prev,
        [`${containerId}_${type}`]: selectedValue,
      }));

      try {
        // Call API
        const updateData =
          type === 'size'
            ? { specification_id: selectedValue.value }
            : { company_id: selectedValue.value };

        await updateContainer(containerId, updateData);

        // Don't remove temp value immediately - let it stay until data refresh
        // The temp value will be overridden by fresh data from server
      } catch (error) {
        // Revert on error
        setTempValues((prev) => {
          const newTemp = { ...prev };
          delete newTemp[`${containerId}_${type}`];
          return newTemp;
        });
      }
    },
    [updateContainer]
  );

  // Define sortable columns for TableLoadList
  const sortableColumns = useMemo(
    () => ({
      size: 'size',
      ssl: 'ssl',
      loc: 'loc',
      name: 'name',
      chases: 'chases',
      return_terminals: 'return_terminals',
      return_dual_terminals: 'return_dual_terminals',
      own_chases: 'own_chases',
      status: 'load_out_last_status',
      gated_out: 'gated_out',
      gated_in: 'gated_in',
      last_free_day: 'last_free_day',
      est_per_diem: 'est_per_diem',
      date: 'date',
      added_date: 'added_date',
      last_update: 'updated_at',
      pickup_appointment: 'load_out_appointment',
      return_appointment: 'return_appointment',
    }),
    []
  );

  // Sort function
  const sortBy = useCallback(
    (key) => {
      let newSortConfig;

      // Three-state sorting: ascending -> descending -> no sort
      if (sortConfig.key === key) {
        // If clicking the same column, cycle through states
        if (sortConfig.direction === 'ascending') {
          // Current: ascending -> Next: descending
          newSortConfig = { key, direction: 'descending' };
        } else if (sortConfig.direction === 'descending') {
          // Current: descending -> Next: no sort (clear)
          newSortConfig = { key: null, direction: '' };
        }
      } else {
        // If clicking a different column, start with ascending
        newSortConfig = { key, direction: 'ascending' };
      }

      // Update state
      setSortConfig(newSortConfig);

      // Save to localStorage
      localStorage.setItem('inventoryTableSort', JSON.stringify(newSortConfig));
    },
    [sortConfig.key, sortConfig.direction]
  );

  // Sorting effect
  useEffect(() => {
    if (sortConfig.key && containersData) {
      const sorted = [...containersData].sort((a, b) => {
        let aValue, bValue;

        // Get values based on the sort key
        if (sortConfig.key === 'size') {
          aValue = a.specification?.size_type || '';
          bValue = b.specification?.size_type || '';
        } else if (sortConfig.key === 'ssl') {
          aValue = a.company?.name || '';
          bValue = b.company?.name || '';
        } else if (sortConfig.key === 'name') {
          aValue = a.name || '';
          bValue = b.name || '';
        } else if (sortConfig.key === 'loc') {
          aValue = a.return_terminals || '';
          bValue = b.return_terminals || '';
        } else if (sortConfig.key === 'return_appointment') {
          // Handle return_appointment - check both direct property and appointments array
          aValue =
            a.return_appointment ||
            a.appointments?.[0]?.terminal_appointment_availability?.date ||
            '';
          bValue =
            b.return_appointment ||
            b.appointments?.[0]?.terminal_appointment_availability?.date ||
            '';

          // Special logic for return_appointment: items with dates first, then items without dates
          const aHasDate = aValue && aValue !== '';
          const bHasDate = bValue && bValue !== '';

          // Always prioritize items with dates over items without dates
          if (aHasDate && !bHasDate) {
            return -1; // a comes first (items with dates always go first)
          }
          if (!aHasDate && bHasDate) {
            return 1; // b comes first (items with dates always go first)
          }

          // If both have dates, sort by date
          if (aHasDate && bHasDate) {
            // Convert dates to comparable format (YYYY-MM-DD)
            const normalizeDate = (dateStr) => {
              // Handle different date formats
              if (dateStr.includes('/')) {
                // Format: MM/DD/YYYY or DD/MM/YYYY
                const parts = dateStr.split('/');
                if (parts.length === 3) {
                  // Assume MM/DD/YYYY format
                  const [month, day, year] = parts;
                  return `${year}-${month.padStart(2, '0')}-${day.padStart(
                    2,
                    '0'
                  )}`;
                }
              } else if (dateStr.includes('-')) {
                // Format: YYYY-MM-DD or MM-DD-YYYY
                const parts = dateStr.split('-');
                if (parts.length === 3) {
                  if (parts[0].length === 4) {
                    // YYYY-MM-DD format
                    return dateStr;
                  } else {
                    // MM-DD-YYYY format
                    const [month, day, year] = parts;
                    return `${year}-${month.padStart(2, '0')}-${day.padStart(
                      2,
                      '0'
                    )}`;
                  }
                }
              }
              return dateStr; // Return as is if can't parse
            };

            const aNormalized = normalizeDate(String(aValue));
            const bNormalized = normalizeDate(String(bValue));

            if (sortConfig.direction === 'ascending') {
              return aNormalized.localeCompare(bNormalized); // Oldest first (ascending)
            } else {
              return bNormalized.localeCompare(aNormalized); // Newest first (descending)
            }
          } else {
            // Both don't have dates - maintain original order
            return 0;
          }
        } else {
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        // Special handling for different data types
        if (
          sortConfig.key === 'updated_at' ||
          sortConfig.key === 'last_free_day' ||
          sortConfig.key === 'date' ||
          sortConfig.key === 'added_date' ||
          sortConfig.key === 'gated_in' ||
          sortConfig.key === 'gated_out' ||
          sortConfig.key === 'pickup_appointment'
        ) {
          // Date sorting
          const aDate = aValue ? new Date(aValue) : new Date(0);
          const bDate = bValue ? new Date(bValue) : new Date(0);

          // Handle invalid dates
          const aTime = isNaN(aDate.getTime()) ? 0 : aDate.getTime();
          const bTime = isNaN(bDate.getTime()) ? 0 : bDate.getTime();

          if (sortConfig.direction === 'ascending') {
            return aTime - bTime;
          } else {
            return bTime - aTime;
          }
        } else if (sortConfig.key === 'est_per_diem') {
          // Number sorting for currency
          const aNum = aValue
            ? parseFloat(String(aValue).replace(/[^0-9.-]/g, '')) || 0
            : 0;
          const bNum = bValue
            ? parseFloat(String(bValue).replace(/[^0-9.-]/g, '')) || 0
            : 0;

          if (sortConfig.direction === 'ascending') {
            return aNum - bNum;
          } else {
            return bNum - aNum;
          }
        } else {
          // String sorting for other columns
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();

          if (aValue < bValue)
            return sortConfig.direction === 'ascending' ? -1 : 1;
          if (aValue > bValue)
            return sortConfig.direction === 'ascending' ? 1 : -1;
          return 0;
        }
      });

      setSortedData(sorted);
    } else {
      setSortedData(containersData);
    }
  }, [containersData, sortConfig]);

  // Get display data
  const getDisplayData = () => {
    return sortConfig.key ? sortedData : containersData;
  };

  // console.log('containersData', containersData);
  return (
    <>
      {containersData[0]?.containers ? (
        <div className="mt-16">
          <LoadingV2 />
        </div>
      ) : (
        <>
          <HintLegend
            hints={[
              {
                letter: 'A',
                label: 'Autobook',
                colorClasses:
                  'text-success-secondary border border-solid border-success-secondary',
              },
              {
                letter: 'M',
                label: 'Manual',
                colorClasses:
                  'text-primary-blue border border-solid border-primary-blue',
              },
              {
                letter: 'E',
                label: 'External',
                colorClasses:
                  'text-inactive-text border border-solid border-inactive-text',
              },
            ]}
          />
          <table ref={tableRef} id="table" className={cls.list}>
            <thead ref={amirbios2Ref} id="amirbios1">
              <tr ref={amirbios1Ref} id="amirbios2" className={cls.thead}>
                <th style={{ flex: 0.4 }}>#</th>
                {tableTheadTitle.map((header, index) =>
                  header?.show === false ? (
                    ''
                  ) : (
                    <th
                      key={index}
                      style={{
                        flex: header.flex,
                        minWidth:
                          header.label === 'Return Appointment'
                            ? '254px'
                            : header.label === 'Pickup Appointment'
                            ? '190px'
                            : '65px',
                      }}
                      className={
                        sortableColumns[header.object] &&
                        sortConfig.key === sortableColumns[header.object]
                          ? styles.sortableHeaderSorted
                          : sortableColumns[header.object]
                          ? styles.sortableHeader
                          : ''
                      }
                      onClick={
                        sortableColumns[header.object]
                          ? () => sortBy(sortableColumns[header.object])
                          : undefined
                      }>
                      <div
                        className={`flex items-center justify-center ${
                          sortableColumns[header.object]
                            ? 'cursor-pointer'
                            : 'cursor-default'
                        }`}
                        onMouseEnter={() =>
                          sortableColumns[header.object] &&
                          setHoveredColumn(sortableColumns[header.object])
                        }
                        onMouseLeave={() => setHoveredColumn(null)}
                        onClick={
                          sortableColumns[header.object]
                            ? () => sortBy(sortableColumns[header.object])
                            : undefined
                        }>
                        <span>{header.label}</span>
                        {sortableColumns[header.object] && (
                          <SortIcon
                            sortConfig={sortConfig}
                            columnKey={sortableColumns[header.object]}
                            hoveredColumn={hoveredColumn}
                            cls={cls}
                            onSort={sortBy}
                            sortKey={sortableColumns[header.object]}
                          />
                        )}
                      </div>
                    </th>
                  )
                )}
                <th style={{ flex: 0.5 }}>
                  <span className={cls.actionsHeadSpan}>
                    <span>Action</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className={cls.tbody}>
              {getDisplayData()?.map((container, idx) => {
                const timerKey = `timer_${container.id}`;

                localUniqueIndex++;
                return (
                  <>
                    <tr className={cls.tableRow} key={`${localUniqueIndex}new`}>
                      <td style={{ flex: 0.4 }}>
                        {localUniqueIndex + (currentPage - 1) * 10}
                      </td>
                      {tableTheadTitle.map((header, index) => {
                        const objectKey = header.object;

                        return header?.show === false ? (
                          ''
                        ) : (
                          <td
                            key={`${index}load`}
                            style={{
                              flex: header.flex,
                              minWidth:
                                header.label === 'Return Appointment'
                                  ? '254px'
                                  : header.label === 'Pickup Appointment'
                                  ? '190px'
                                  : '65px',
                              width: 'content-fit',
                            }}
                            className="break-words">
                            {['size'].includes(objectKey) ? (
                              container.additional?.cabin_not_verified ? (
                                <CustomSelectEx
                                  placeholder="Select Size"
                                  options={Sizes}
                                  value={
                                    tempValues[`${container.id}_size`] ||
                                    (container.specification?.size_type
                                      ? Sizes.find(
                                          (size) =>
                                            size.label ===
                                            container.specification.size_type
                                        )
                                      : null)
                                  }
                                  onChange={(selectedSize) => {
                                    handleSelectChange(
                                      container.id,
                                      'size',
                                      selectedSize
                                    );
                                  }}
                                  mini={true}
                                  height="28px"
                                  fontSize="11px"
                                  width="62px"
                                  dropdown={true}
                                />
                              ) : container.specification?.size_type ? (
                                container.specification.size_type
                              ) : (
                                '-'
                              )
                            ) : (
                              ''
                            )}
                            {['ssl'].includes(objectKey) ? (
                              container.additional?.cabin_not_verified ? (
                                <CustomSelectEx
                                  placeholder="Select SSL"
                                  options={SSLs}
                                  value={
                                    tempValues[`${container.id}_ssl`] ||
                                    (container.company?.name
                                      ? SSLs.find(
                                          (ssl) =>
                                            ssl.label === container.company.name
                                        )
                                      : null)
                                  }
                                  onChange={(selectedSSL) => {
                                    handleSelectChange(
                                      container.id,
                                      'ssl',
                                      selectedSSL
                                    );
                                  }}
                                  mini={true}
                                  height="28px"
                                  fontSize="11px"
                                  width="62px"
                                  dropdown={true}
                                />
                              ) : container.company?.name ? (
                                container.company.name
                              ) : (
                                '-'
                              )
                            ) : (
                              ''
                            )}
                            {['loc'].includes(objectKey)
                              ? container.return_terminals
                                ? container.return_terminals
                                : '-'
                              : ''}
                            {['name'].includes(objectKey) && header.hasLink ? (
                              <a
                                className={cls.listHasLink}
                                href={container['shipping_line_tracking_url']}
                                onClick={(e) =>
                                  handleContainerNameClick(
                                    e,
                                    container,
                                    objectKey
                                  )
                                }>
                                {container[objectKey] || '-'}
                              </a>
                            ) : ['name'].includes(objectKey) ? (
                              container[objectKey] ? (
                                container[objectKey]
                              ) : (
                                '-'
                              )
                            ) : (
                              ''
                            )}

                            {[
                              'chases',
                              'return_terminals',
                              'return_dual_terminals',
                            ].includes(objectKey)
                              ? container[objectKey]
                                ? container[objectKey]
                                : '-'
                              : ''}
                            {objectKey === 'own_chases'
                              ? container['own_chases'] === 0
                                ? 'Pool'
                                : 'Own'
                              : ''}
                            {objectKey === 'return_appointment' ? (
                              container.return_appointment ? (
                                formatDateTimeToUTC(
                                  container.return_appointment
                                )
                              ) : enable_appointments === '0' ||
                                enable_appointments === false ? (
                                'No Apt. Available'
                              ) : (
                                <div
                                  className={cls.appointmentDateContainer}
                                  onClick={() => {
                                    if (
                                      container.cabin &&
                                      container.specification
                                    ) {
                                      Appointment(
                                        container,
                                        container.id,
                                        container.gated_in,
                                        container.gated_out,
                                        container
                                      );
                                    } else {
                                      SaveApiResMessage({
                                        title: 'Warning',
                                        message:
                                          'Please select the container size and type to proceed with booking the return appointment.',
                                        mode: 'warning',
                                      });
                                    }
                                  }}>
                                  {container?.appointments?.length > 0 ? (
                                    <AppointmentContainer
                                      container={container}
                                      cls={cls}
                                      Appointment={Appointment}
                                      i={container}
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      className={`${cls.appointmentButton} cursor-pointer bg-white flex items-center justify-center`}>
                                      <div className={cls.svgContainer} />
                                      {container.autobook_on ? (
                                        <>
                                          <AppointmentButtonIcon
                                            type="autobook"
                                            cls={cls}
                                          />
                                          <span className={cls.autobookOnText}>
                                            Autobook
                                          </span>
                                        </>
                                      ) : container?.available_appointments_count >
                                        0 ? (
                                        <>
                                          <AppointmentButtonIcon
                                            type="book"
                                            cls={cls}
                                          />
                                          <span className={cls.appointmenText}>
                                            Book Now
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <AppointmentButtonIcon
                                            type="notAvailable"
                                            cls={cls}
                                          />
                                          <span className={cls.appointmenText}>
                                            Not Avail.
                                          </span>
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              )
                            ) : (
                              ''
                            )}
                            {objectKey === 'pickup_appointment' ? (
                              container?.load_out_appointment ? (
                                <div
                                  className={
                                    cls.appointmentDateContainerLoadOut
                                  }
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    padding: '4px 0',
                                    gap: '5px',
                                  }}>
                                  <PickupAppointmentIcon
                                    status={container?.load_out_last_status}
                                  />
                                  <div
                                    style={{
                                      padding: 0,
                                      fontFamily: 'Averta light',
                                      fontWeight: '300',
                                      fontSize: '12px',
                                      color: '#131517',
                                    }}>
                                    {container?.load_out_appointment?.replace(
                                      /(\d{4})-(\d{2})-(\d{2})/,
                                      '$2-$3-$1'
                                    )}
                                  </div>
                                  <span
                                    style={{
                                      fontFamily: 'Averta',
                                      color: '#131517',
                                      fontWeight: '700',
                                      fontSize: '12px',
                                    }}
                                    className={cls.appointmenTerminal}>
                                    {container?.load_out_terminal?.slug}
                                  </span>{' '}
                                </div>
                              ) : (
                                'No Apt. Available'
                              )
                            ) : (
                              ''
                            )}
                            {objectKey === 'status' ? (
                              container['load_out_last_status'] ? (
                                <span className={cls.statusLoadContainer}>
                                  {' '}
                                  {container['load_out_last_status']}
                                </span>
                              ) : (
                                '-'
                              )
                            ) : (
                              ''
                            )}
                            {/* {objectKey === 'gated_out' ? (
                            container.scr_last !== null ||
                            container.gated_in !== null ||
                            container.gated_out !== null ||
                            (timerinterval[timerKey] &&
                              timerinterval[timerKey] > 600) ||
                            container.create_type === 'SYSTEMIC' ? (
                              container.gated_out ? (
                                formatDateTimeToUTC(container.gated_out)
                              ) : (
                                '-'
                              )
                            ) : (
                              <CircularLoader
                                timerKey={timerKey}
                                timerinterval={timerinterval}
                                fastProgress={fastProgress}
                                cls={cls}
                              />
                            )
                          ) : (
                            ''
                          )} */}
                            {objectKey === 'gated_out'
                              ? container['gated_out']
                                ? formatDateTimeToUTC(container['gated_out'])
                                : '-'
                              : ''}
                            {objectKey === 'gated_in'
                              ? container['gated_in']
                                ? formatDateTimeToUTC(container['gated_in'])
                                : '-'
                              : ''}
                            {objectKey === 'last_free_day'
                              ? container['last_free_day']
                                ? formatDateToUTC(container['last_free_day'])
                                : '-'
                              : ''}
                            {objectKey === 'est_per_diem'
                              ? container['est_per_diem']
                                ? formatPrice(container['est_per_diem'])
                                : '-'
                              : ''}
                            {objectKey === 'date'
                              ? container['date']
                                ? fDate(container['date'])
                                : '-'
                              : ''}
                            {objectKey === 'added_date'
                              ? container['added_date']
                                ? fTimeDateUSFormat(container['added_date'])
                                : '-'
                              : ''}
                            {objectKey === 'last_update'
                              ? container['updated_at']
                                ? fTimeDateUSFormat(container['updated_at'])
                                : '-'
                              : ''}
                          </td>
                        );
                      })}

                      <td className={cls.tabActions} style={{ flex: 0.5 }}>
                        <IconButtonV2
                          icon="deleteIcon"
                          aria-label="deleteIcon"
                          title="Delete"
                          onClick={() => handleDeleteClick(container.id)}
                          noBorder
                        />
                      </td>
                    </tr>
                  </>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        minWidth="unset"
        noX
        show={showDeleteModal.show}
        children={
          <span className="mt-6 text-center w-full md:min-w-xl">
            Are you sure you want to delete this container?
          </span>
        }
        submitText={'Yes, Delete'}
        icon={'deleteIcon'}
        title={'Delete Container'}
        setShow={(value) => {
          setShowDeleteModal({
            show: value,
            containerId: showDeleteModal.containerId,
          });
        }}
        submit={handleDeleteConfirm}
        loadingbutton={false}
      />
    </>
  );
};

export default TableLoadList;
